import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Nav, Alert, Badge, Image, Spinner } from 'react-bootstrap';
import { FaUser, FaEdit, FaKey, FaStore, FaShoppingBag, FaHeart, FaEnvelope, FaPhone, FaIdBadge, FaBuilding, FaGraduationCap, FaMapMarkerAlt, FaCamera, FaCog } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { getOrders } from '../services/orderService'; // Import the order service
import { getSellerProducts } from '../services/productService'; // Import the product service
import { updateUserProfile, changePassword } from '../services/authService'; // Import the updateUserProfile function
import { getWishlist, removeFromWishlist } from '../services/wishlistService'; // Import wishlist service
import { Link } from 'react-router-dom'; // Added for Link
import './Profile.css';

const Profile = () => {
  const { currentUser, userRole, updateProfile } = useAuth();
  const [activeSection, setActiveSection] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [preview, setPreview] = useState(
    currentUser?.profile_picture_preview || 
    currentUser?.profile_picture || 
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2FkYjViZCI+PHBhdGggZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bTAgM2MxLjY2IDAgMyAxLjM0IDMgM3MtMS4zNCAzLTMgMy0zLTEuMzQtMy0zIDEuMzQtMyAzLTN6bTAgMTRjLTIuNjcgMC04IDEuMzQtOCA0djJoMTZ2LTJjMC0yLjY2LTUuMzMtNC04LTR6Ii8+PC9zdmc+'
  );
  const [formData, setFormData] = useState({
    first_name: currentUser?.first_name || '',
    last_name: currentUser?.last_name || '',
    email: currentUser?.email || '',
    role: currentUser?.role || '',
    seller_type: currentUser?.seller_type || '',
    university: currentUser?.university?.name || '',
    campus: currentUser?.campus?.name || '',
    phone_number: currentUser?.phone_number || '',
    location: currentUser?.location || '',
    student_id: currentUser?.student_id || '',
    level: currentUser?.level || '',
    program_of_study: currentUser?.program_of_study || '',
    business_name: currentUser?.business_name || '',
    business_description: currentUser?.business_description || '',
    bio: currentUser?.bio || '',
    profile_picture: currentUser?.profile_picture || '',
  });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const fileInputRef = useRef(null);

  // State for orders
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState('');
  
  // State for seller products
  const [sellerProducts, setSellerProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState('');
  
  // State for wishlist
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  const [wishlistError, setWishlistError] = useState('');

  useEffect(() => {
    if (currentUser) {
      setFormData({
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        email: currentUser.email || '',
        role: currentUser.role || '',
        seller_type: currentUser.seller_type || '',
        university: currentUser.university?.name || '',
        campus: currentUser.campus?.name || '',
        phone_number: currentUser.phone_number || '',
        location: currentUser.location || '',
        student_id: currentUser.student_id || '',
        level: currentUser.level || '',
        program_of_study: currentUser.program_of_study || '',
        business_name: currentUser.business_name || '',
        business_description: currentUser.business_description || '',
        bio: currentUser.bio || '',
        profile_picture: currentUser.profile_picture || '',
      });
      setPreview(currentUser?.profile_picture_preview || currentUser?.profile_picture || 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2FkYjViZCI+PHBhdGggZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bTAgM2MxLjY2IDAgMyAxLjM0IDMgM3MtMS4zNCAzLTMgMy0zLTEuMzQtMy0zIDEuMzQtMyAzLTN6bTAgMTRjLTIuNjcgMC04IDEuMzQtOCA0djJoMTZ2LTJjMC0yLjY2LTUuMzMtNC04LTR6Ii8+PC9zdmc+');
    }

    // Fetch orders
    const fetchOrders = async () => {
      setOrdersLoading(true);
      try {
        const fetchedOrders = await getOrders();
        setOrders(fetchedOrders.slice(0, 2)); // Show a smaller number of recent orders on profile
      } catch (err) {
        setOrdersError('Could not load recent orders.');
      } finally {
        setOrdersLoading(false);
      }
    };

    // Fetch seller products if user is a seller
    const fetchSellerProducts = async () => {
      if (currentUser?.role === 'seller') {
        setProductsLoading(true);
        try {
          const response = await getSellerProducts();
          // Filter products for the current seller
          const products = response.results || [];
          const filteredProducts = products.filter(
            product => product.seller && String(product.seller.id) === String(currentUser.id)
          );
          setSellerProducts(filteredProducts);
        } catch (err) {
          setProductsError('Could not load your products.');
        } finally {
          setProductsLoading(false);
        }
      }
    };

    // Fetch wishlist
    const fetchWishlist = async () => {
      setWishlistLoading(true);
      try {
        const wishlist = await getWishlist();
        setWishlistItems(wishlist);
      } catch (err) {
        setWishlistError('Could not load wishlist.');
      } finally {
        setWishlistLoading(false);
      }
    };

    fetchOrders();
    fetchSellerProducts();
    fetchWishlist();

  }, [currentUser]);

  // Update handleFormChange to use snake_case field names
  const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handlePasswordChange = (e) => setPasswordData({ ...passwordData, [e.target.name]: e.target.value });

  // Update handleUpdateProfile to use the new field names
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const profileData = { ...formData };
      await updateProfile(profileData);
      setSuccess('Profile updated successfully!');
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Password changed successfully!');
    } catch (err) {
      setError(err.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  const handlePictureClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
      // Simulate immediate upload
      handlePictureUpload(file);
    }
  };

  const handlePictureUpload = async (file) => {
    if (!file) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // Create a simple FormData with just the file
      const formData = new FormData();
      formData.append('profile_picture', file);
      
      // Make the API call directly
      const updatedUser = await updateUserProfile(formData);
      
      if (updatedUser && updatedUser.profile_picture) {
        // If the server returns a URL, use it
        setPreview(updatedUser.profile_picture);
        setSuccess('Profile picture updated successfully!');
      } else {
        // Create a local preview if server doesn't return a URL
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
          setSuccess('Profile picture updated successfully!');
        };
        reader.readAsDataURL(file);
      }
    } catch (err) {
      setError(err.message || 'Failed to upload profile picture.');
      // Restore previous preview if upload fails
      setPreview(
        currentUser?.profile_picture_preview || 
        currentUser?.profile_picture || 
        'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2FkYjViZCI+PHBhdGggZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bTAgM2MxLjY2IDAgMyAxLjM0IDMgM3MtMS4zNCAzLTMgMy0zLTEuMzQtMy0zIDEuMzQtMyAzLTN6bTAgMTRjLTIuNjcgMC04IDEuMzQtOCA0djJoMTZ2LTJjMC0yLjY2LTUuMzMtNC04LTR6Ii8+PC9zdmc+'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    try {
      await removeFromWishlist(productId);
      setWishlistItems(wishlistItems.filter(item => item.product.id !== productId));
      setSuccess('Item removed from wishlist');
    } catch (err) {
      setError(err.message || 'Failed to remove item from wishlist');
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'profile': return <ProfileSection />;
      case 'listings': return <ListingsSection />;
      case 'orders': return <OrdersSection />;
      case 'wishlist': return <WishlistSection />;
      case 'security': return <SecuritySection />;
      default: return null;
    }
  };

  const ProfileSection = () => (
    <Card className="shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Profile Information</h5>
        <Button variant="outline-primary" size="sm" onClick={() => setEditing(!editing)}><FaEdit className="me-2" />{editing ? 'Cancel' : 'Edit Profile'}</Button>
      </Card.Header>
      <Card.Body className="p-4">
        {editing ? <ProfileForm /> : <ProfileDetails />}
      </Card.Body>
    </Card>
  );
  
  // Helper to determine which fields to show
  const isBuyer = formData.role === 'buyer';
  const isSellerStudent = formData.role === 'seller' && formData.seller_type === 'student';
  const isSellerProfessional = formData.role === 'seller' && formData.seller_type === 'professional';
  const isAdmin = formData.role === 'admin';

  // Update ProfileDetails to show fields based on user type
  const ProfileDetails = () => (
    <Row>
      <Col md={6} className="info-row"><FaUser /><p><strong>First Name:</strong> {formData.first_name}</p></Col>
      <Col md={6} className="info-row"><FaUser /><p><strong>Last Name:</strong> {formData.last_name}</p></Col>
        <Col md={6} className="info-row"><FaEnvelope /><p><strong>Email:</strong> {formData.email}</p></Col>
      <Col md={6} className="info-row"><FaPhone /><p><strong>Phone:</strong> {formData.phone_number}</p></Col>
      <Col md={6} className="info-row"><FaMapMarkerAlt /><p><strong>Location:</strong> {formData.location}</p></Col>
      {isSellerStudent && <>
        <Col md={6} className="info-row"><FaIdBadge /><p><strong>Student ID:</strong> {formData.student_id}</p></Col>
        <Col md={6} className="info-row"><FaBuilding /><p><strong>Program of Study:</strong> {formData.program_of_study}</p></Col>
        <Col md={6} className="info-row"><FaGraduationCap /><p><strong>Level:</strong> {formData.level}</p></Col>
        <Col md={6} className="info-row"><FaBuilding /><p><strong>University:</strong> {formData.university}</p></Col>
        <Col md={6} className="info-row"><FaBuilding /><p><strong>Campus:</strong> {formData.campus}</p></Col>
      </>}
      {isSellerProfessional && <>
        <Col md={6} className="info-row"><FaStore /><p><strong>Business Name:</strong> {formData.business_name}</p></Col>
        <Col md={6} className="info-row"><FaStore /><p><strong>Business Description:</strong> {formData.business_description}</p></Col>
      </>}
      {isAdmin && <>
        <Col md={6} className="info-row"><FaIdBadge /><p><strong>Student ID:</strong> {formData.student_id}</p></Col>
        <Col md={6} className="info-row"><FaBuilding /><p><strong>Program of Study:</strong> {formData.program_of_study}</p></Col>
        <Col md={6} className="info-row"><FaGraduationCap /><p><strong>Level:</strong> {formData.level}</p></Col>
        <Col md={6} className="info-row"><FaStore /><p><strong>Business Name:</strong> {formData.business_name}</p></Col>
        <Col md={6} className="info-row"><FaStore /><p><strong>Business Description:</strong> {formData.business_description}</p></Col>
        <Col md={6} className="info-row"><FaUser /><p><strong>Role:</strong> {formData.role}</p></Col>
        <Col md={6} className="info-row"><FaUser /><p><strong>Seller Type:</strong> {formData.seller_type}</p></Col>
        <Col md={6} className="info-row"><FaBuilding /><p><strong>University:</strong> {formData.university}</p></Col>
        <Col md={6} className="info-row"><FaBuilding /><p><strong>Campus:</strong> {formData.campus}</p></Col>
      </>}
        <Col md={12}><p><strong>Bio:</strong> {formData.bio || 'No bio provided.'}</p></Col>
    </Row>
  );

  // Update ProfileForm to show fields based on user type
  const ProfileForm = () => (
    <Form onSubmit={handleUpdateProfile}>
        <Row>
        <Col md={6}><Form.Group className="mb-3"><Form.Label>First Name</Form.Label><Form.Control type="text" name="first_name" value={formData.first_name} onChange={handleFormChange} /></Form.Group></Col>
        <Col md={6}><Form.Group className="mb-3"><Form.Label>Last Name</Form.Label><Form.Control type="text" name="last_name" value={formData.last_name} onChange={handleFormChange} /></Form.Group></Col>
            <Col md={6}><Form.Group className="mb-3"><Form.Label>Email</Form.Label><Form.Control type="email" value={formData.email} disabled /></Form.Group></Col>
        <Col md={6}><Form.Group className="mb-3"><Form.Label>Phone Number</Form.Label><Form.Control type="text" name="phone_number" value={formData.phone_number} onChange={handleFormChange} /></Form.Group></Col>
        <Col md={6}><Form.Group className="mb-3"><Form.Label>Location</Form.Label><Form.Control type="text" name="location" value={formData.location} onChange={handleFormChange} /></Form.Group></Col>
        {isSellerStudent && <>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Student ID</Form.Label><Form.Control type="text" name="student_id" value={formData.student_id} onChange={handleFormChange} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Program of Study</Form.Label><Form.Control type="text" name="program_of_study" value={formData.program_of_study} onChange={handleFormChange} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Level</Form.Label><Form.Control type="text" name="level" value={formData.level} onChange={handleFormChange} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>University</Form.Label><Form.Control type="text" name="university" value={formData.university} disabled /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Campus</Form.Label><Form.Control type="text" name="campus" value={formData.campus} disabled /></Form.Group></Col>
        </>}
        {isSellerProfessional && <>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Business Name</Form.Label><Form.Control type="text" name="business_name" value={formData.business_name} onChange={handleFormChange} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Business Description</Form.Label><Form.Control type="text" name="business_description" value={formData.business_description} onChange={handleFormChange} /></Form.Group></Col>
        </>}
        {isAdmin && <>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Student ID</Form.Label><Form.Control type="text" name="student_id" value={formData.student_id} onChange={handleFormChange} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Program of Study</Form.Label><Form.Control type="text" name="program_of_study" value={formData.program_of_study} onChange={handleFormChange} /></Form.Group></Col>
            <Col md={6}><Form.Group className="mb-3"><Form.Label>Level</Form.Label><Form.Control type="text" name="level" value={formData.level} onChange={handleFormChange} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Business Name</Form.Label><Form.Control type="text" name="business_name" value={formData.business_name} onChange={handleFormChange} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Business Description</Form.Label><Form.Control type="text" name="business_description" value={formData.business_description} onChange={handleFormChange} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Role</Form.Label><Form.Control type="text" name="role" value={formData.role} disabled /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Seller Type</Form.Label><Form.Control type="text" name="seller_type" value={formData.seller_type} onChange={handleFormChange} /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>University</Form.Label><Form.Control type="text" name="university" value={formData.university} disabled /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>Campus</Form.Label><Form.Control type="text" name="campus" value={formData.campus} disabled /></Form.Group></Col>
        </>}
            <Col md={12}><Form.Group className="mb-3"><Form.Label>Bio</Form.Label><Form.Control as="textarea" rows={3} name="bio" value={formData.bio} onChange={handleFormChange} /></Form.Group></Col>
        </Row>
        <Button type="submit" variant="success" disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</Button>
    </Form>
  );

  const SecuritySection = () => (
    <Card className="shadow-sm">
        <Card.Header><h5 className="mb-0">Change Password</h5></Card.Header>
        <Card.Body className="p-4">
            <Form onSubmit={handleChangePassword}>
                <Form.Group className="mb-3"><Form.Label>Current Password</Form.Label><Form.Control type="password" name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} /></Form.Group>
                <Form.Group className="mb-3"><Form.Label>New Password</Form.Label><Form.Control type="password" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} /></Form.Group>
                <Form.Group className="mb-3"><Form.Label>Confirm New Password</Form.Label><Form.Control type="password" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} /></Form.Group>
                <Button type="submit" variant="danger" disabled={loading}>{loading ? 'Updating...' : 'Update Password'}</Button>
            </Form>
        </Card.Body>
    </Card>
  );
  
  const ListingsSection = () => {
    if (productsLoading) {
      return <div className="text-center p-5"><Spinner animation="border" /></div>;
    }
    
    if (productsError) {
      return <Alert variant="danger">{productsError}</Alert>;
    }
    
    if (sellerProducts.length === 0) {
      return (
    <Card className="shadow-sm">
        <Card.Header><h5 className="mb-0">My Listings</h5></Card.Header>
          <Card.Body className="text-center p-5">
            <p>You don't have any products listed yet.</p>
            <Button as={Link} to="/seller/add-product" variant="primary">Add Your First Product</Button>
          </Card.Body>
        </Card>
      );
    }
    
    return (
      <Card className="shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">My Listings</h5>
          <Button as={Link} to="/seller/products" variant="outline-primary" size="sm">View All</Button>
        </Card.Header>
        <Card.Body>
          <div className="product-listings-container">
            <Row className="g-3">
              {sellerProducts.slice(0, 6).map(product => (
                <Col sm={12} md={6} lg={4} key={product.id} className="mb-3">
                  <Card className="h-100 product-card">
                    <Card.Img 
                      variant="top" 
                      src={product.images && product.images.length > 0 
                        ? product.images[0].image 
                        : `https://picsum.photos/seed/${encodeURIComponent(product.name)}/400/300`} 
                      style={{ height: '180px', objectFit: 'cover' }} 
                    />
                            <Card.Body>
                      <Card.Title className="product-title">{product.name}</Card.Title>
                      <Card.Text className="text-muted">GH₵ {parseFloat(product.price).toFixed(2)}</Card.Text>
                      <Badge bg={product.status === 'active' ? 'success' : 'secondary'}>
                        {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                      </Badge>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>
          </div>
        </Card.Body>
    </Card>
  );
  };
  const OrdersSection = () => {
    if (ordersLoading) {
      return <div className="text-center p-5"><Spinner animation="border" /></div>;
    }

    if (ordersError) {
      return <Alert variant="danger">{ordersError}</Alert>;
    }

    return (
      <Card className="shadow-sm">
          <Card.Header><h5 className="mb-0">My Orders</h5></Card.Header>
          <Card.Body>
              {orders.length > 0 ? orders.map(o => (
                  <div key={o.id} className="d-flex align-items-center mb-3 p-3 border rounded">
                      <Image src={o.image} width={80} height={80} className="me-3 rounded" style={{objectFit: 'cover'}} />
                      <div className="flex-grow-1">
                          <h6 className="mb-1">{o.product}</h6>
                          <p className="mb-1 text-muted">From: {o.seller}</p>
                          <p className="mb-1 fw-bold">{o.price}</p>
                      </div>
                      <Badge bg={o.status === 'Delivered' ? 'success' : (o.status === 'Shipped' ? 'info' : 'secondary')}>{o.status}</Badge>
                  </div>
              )) : <p>You have no recent orders.</p>}
          </Card.Body>
      </Card>
    );
  };
  const WishlistSection = () => {
    if (wishlistLoading) {
      return <div className="text-center p-5"><Spinner animation="border" /></div>;
    }

    if (wishlistError) {
      return <Alert variant="danger">{wishlistError}</Alert>;
    }

    return (
      <Card className="shadow-sm">
        <Card.Header><h5 className="mb-0">My Wishlist</h5></Card.Header>
        <Card.Body>
          {wishlistItems.length > 0 ? (
            <Row>
              {wishlistItems.map(item => (
                <Col md={6} lg={4} key={item.id} className="mb-4">
                  <Card className="h-100">
                    <Card.Img 
                      variant="top" 
                      src={item.product?.images?.[0]?.image || 'https://via.placeholder.com/400x300?text=No+Image'} 
                      style={{ height: '180px', objectFit: 'cover' }} 
                    />
                    <Card.Body className="d-flex flex-column">
                      <Card.Title>{item.product?.name || 'Product'}</Card.Title>
                      <Card.Text className="fw-bold">
                        GH₵ {item.product?.price ? parseFloat(item.product.price).toFixed(2) : '0.00'}
                      </Card.Text>
                      <Button 
                        variant="outline-danger" 
                        size="sm" 
                        className="mt-auto"
                        onClick={() => handleRemoveFromWishlist(item.product.id)}
                      >
                        Remove
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <p className="text-center text-muted py-4">Your wishlist is empty</p>
          )}
        </Card.Body>
      </Card>
    );
  };

  if (!currentUser) return <Layout><Container className="text-center py-5"><Spinner animation="border" /></Container></Layout>;

  return (
    <Layout>
      <div className="profile-shell">
        <div className="profile-hero">
          <Container className="py-4">
            <div className="profile-hero-card">
              <div className="profile-cover" />
              <div className="profile-hero-inner">
                <div className="profile-avatar" onClick={handlePictureClick}>
                  <Image src={preview} roundedCircle className="profile-avatar-image" />
                  <div className="profile-avatar-overlay">
                    <FaCamera />
                  </div>
                </div>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileChange} 
                  className="hidden-file-input" 
                  accept="image/*"
                />
                <div className="profile-identity">
                  <h2 className="profile-hero-name">{formData.first_name} {formData.last_name}</h2>
                  <div className="profile-hero-meta">
                    <Badge bg="light" text="dark" className="me-2 text-capitalize">{userRole}</Badge>
                    <span className="profile-hero-email"><FaEnvelope className="me-2" />{formData.email}</span>
                  </div>
                  <div className="profile-hero-actions">
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => {
                        setActiveSection('profile');
                        setEditing(true);
                      }}
                    >
                      <FaEdit className="me-2" /> Edit Profile
                    </Button>
                    <Button
                      as={Link}
                      to="/settings"
                      variant="outline-light"
                      size="sm"
                      className="ms-2"
                    >
                      <FaCog className="me-2" /> Settings
                    </Button>
                  </div>
                </div>
                <div className="profile-hero-stats">
                  {userRole === 'seller' && (
                    <div className="profile-stat" onClick={() => setActiveSection('listings')} role="button" tabIndex={0}>
                      <div className="profile-stat-value">{sellerProducts.length}</div>
                      <div className="profile-stat-label"><FaStore className="me-2" />Listings</div>
                    </div>
                  )}
                  <div className="profile-stat" onClick={() => setActiveSection('orders')} role="button" tabIndex={0}>
                    <div className="profile-stat-value">{orders.length}</div>
                    <div className="profile-stat-label"><FaShoppingBag className="me-2" />Orders</div>
                  </div>
                  <div className="profile-stat" onClick={() => setActiveSection('wishlist')} role="button" tabIndex={0}>
                    <div className="profile-stat-value">{wishlistItems.length}</div>
                    <div className="profile-stat-label"><FaHeart className="me-2" />Wishlist</div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>

        <Container className="pb-4">
          <Row className="g-4">
            <Col lg={3}>
              <Card className="profile-nav-card">
                <Card.Body className="p-2">
                  <Nav
                    className="profile-nav"
                    variant="pills"
                    activeKey={activeSection}
                    onSelect={(k) => setActiveSection(k)}
                  >
                    <Nav.Link eventKey="profile" className="d-flex align-items-center">
                      <FaUser className="me-2" /> <span className="nav-text">My Profile</span>
                    </Nav.Link>
                    {userRole === 'seller' && (
                      <Nav.Link eventKey="listings" className="d-flex align-items-center">
                        <FaStore className="me-2" /> <span className="nav-text">My Listings</span>
                      </Nav.Link>
                    )}
                    <Nav.Link eventKey="orders" className="d-flex align-items-center">
                      <FaShoppingBag className="me-2" /> <span className="nav-text">My Orders</span>
                    </Nav.Link>
                    <Nav.Link eventKey="wishlist" className="d-flex align-items-center">
                      <FaHeart className="me-2" /> <span className="nav-text">Wishlist</span>
                    </Nav.Link>
                    <Nav.Link eventKey="security" className="d-flex align-items-center">
                      <FaKey className="me-2" /> <span className="nav-text">Security</span>
                    </Nav.Link>
                  </Nav>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={9}>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              <div className="profile-content-inner">
                {renderSection()}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

export default Profile;